// src/pages/LMS/Gamification/BadgeManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Grid, Card, CardContent,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Chip, FormControl, InputLabel, Select, MenuItem,
  Avatar, Stack, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const BadgeManagement = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, badge: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, badge: null });

  const [badgeForm, setBadgeForm] = useState({
    name: '',
    description: '',
    icon: 'star',
    points_required: 0,
    criteria_type: 'points',
    criteria_value: 0,
    is_active: true
  });

  const iconOptions = [
    { value: 'star', label: 'Star', icon: '⭐' },
    { value: 'trophy', label: 'Trophy', icon: '🏆' },
    { value: 'medal', label: 'Medal', icon: '🏅' },
    { value: 'crown', label: 'Crown', icon: '👑' },
    { value: 'fire', label: 'Fire', icon: '🔥' },
    { value: 'rocket', label: 'Rocket', icon: '🚀' },
    { value: 'target', label: 'Target', icon: '🎯' },
    { value: 'diamond', label: 'Diamond', icon: '💎' }
  ];

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/lms/badges');
      setBadges(response.data.data);
    } catch (error) {
      console.error('Error loading badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBadge = async () => {
    if (!badgeForm.name || !badgeForm.description) {
      alert('Name and description are required');
      return;
    }

    try {
      await axiosInstance.post('/api/lms/badges', badgeForm);
      setCreateDialog(false);
      resetForm();
      loadBadges();
    } catch (error) {
      console.error('Error creating badge:', error);
    }
  };

  const handleUpdateBadge = async () => {
    if (!editDialog.badge) return;

    try {
      await axiosInstance.put(`/api/lms/badges/${editDialog.badge.id}`, badgeForm);
      setEditDialog({ open: false, badge: null });
      resetForm();
      loadBadges();
    } catch (error) {
      console.error('Error updating badge:', error);
    }
  };

  const handleDeleteBadge = async () => {
    if (!deleteDialog.badge) return;

    try {
      await axiosInstance.delete(`/api/lms/badges/${deleteDialog.badge.id}`);
      setDeleteDialog({ open: false, badge: null });
      loadBadges();
    } catch (error) {
      console.error('Error deleting badge:', error);
    }
  };

  const resetForm = () => {
    setBadgeForm({
      name: '',
      description: '',
      icon: 'star',
      points_required: 0,
      criteria_type: 'points',
      criteria_value: 0,
      is_active: true
    });
  };

  const getIconEmoji = (iconValue) => {
    const icon = iconOptions.find(opt => opt.value === iconValue);
    return icon ? icon.icon : '⭐';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}>
            <BackIcon />
          </IconButton>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h4" fontWeight={700}>
              <TrophyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Badge Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage achievement badges for students
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          Create Badge
        </Button>
      </Box>

      {/* Badges Grid */}
      <Grid container spacing={3}>
        {badges.map((badge) => (
          <Grid item xs={12} sm={6} md={4} key={badge.id}>
            <Card sx={{ position: 'relative' }}>
              <CardContent>
                {/* Active Status */}
                <Chip
                  label={badge.is_active ? 'Active' : 'Inactive'}
                  color={badge.is_active ? 'success' : 'default'}
                  size="small"
                  sx={{ position: 'absolute', top: 16, right: 16 }}
                />

                {/* Badge Icon */}
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'warning.main',
                      mx: 'auto',
                      fontSize: '2.5rem'
                    }}
                  >
                    {getIconEmoji(badge.icon)}
                  </Avatar>
                </Box>

                {/* Badge Info */}
                <Typography variant="h6" align="center" gutterBottom>
                  {badge.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {badge.description}
                </Typography>

                {/* Criteria */}
                <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Criteria:
                  </Typography>
                  <Typography variant="body2">
                    {badge.criteria_type === 'points' && `Earn ${badge.criteria_value} points`}
                    {badge.criteria_type === 'courses_completed' && 
                      `Complete ${badge.criteria_value} courses`}
                    {badge.criteria_type === 'perfect_scores' && 
                      `Achieve ${badge.criteria_value} perfect test scores`}
                    {badge.criteria_type === 'streak_days' && 
                      `Maintain ${badge.criteria_value} day login streak`}
                  </Typography>
                </Box>

                {/* Stats */}
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {badge.earned_count || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Earned
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      +{badge.points_required}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Points
                    </Typography>
                  </Box>
                </Stack>

                {/* Actions */}
                <Stack direction="row" spacing={1} justifyContent="center">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setBadgeForm({
                        name: badge.name,
                        description: badge.description,
                        icon: badge.icon,
                        points_required: badge.points_required,
                        criteria_type: badge.criteria_type,
                        criteria_value: badge.criteria_value,
                        is_active: badge.is_active
                      });
                      setEditDialog({ open: true, badge });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialog({ open: true, badge })}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {badges.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <TrophyIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Badges Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first achievement badge to motivate students
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
          >
            Create First Badge
          </Button>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Badge</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Badge Name"
                value={badgeForm.name}
                onChange={(e) => setBadgeForm({ ...badgeForm, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={badgeForm.description}
                onChange={(e) => setBadgeForm({ ...badgeForm, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={badgeForm.icon}
                  onChange={(e) => setBadgeForm({ ...badgeForm, icon: e.target.value })}
                  label="Icon"
                >
                  {iconOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.icon} {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Points Reward"
                value={badgeForm.points_required}
                onChange={(e) => setBadgeForm({
                  ...badgeForm,
                  points_required: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Criteria Type</InputLabel>
                <Select
                  value={badgeForm.criteria_type}
                  onChange={(e) => setBadgeForm({ ...badgeForm, criteria_type: e.target.value })}
                  label="Criteria Type"
                >
                  <MenuItem value="points">Total Points</MenuItem>
                  <MenuItem value="courses_completed">Courses Completed</MenuItem>
                  <MenuItem value="perfect_scores">Perfect Test Scores</MenuItem>
                  <MenuItem value="streak_days">Login Streak (Days)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Criteria Value"
                value={badgeForm.criteria_value}
                onChange={(e) => setBadgeForm({
                  ...badgeForm,
                  criteria_value: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateBadge}>
            Create Badge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, badge: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Badge</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Badge Name"
                value={badgeForm.name}
                onChange={(e) => setBadgeForm({ ...badgeForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={badgeForm.description}
                onChange={(e) => setBadgeForm({ ...badgeForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={badgeForm.icon}
                  onChange={(e) => setBadgeForm({ ...badgeForm, icon: e.target.value })}
                  label="Icon"
                >
                  {iconOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.icon} {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Points Reward"
                value={badgeForm.points_required}
                onChange={(e) => setBadgeForm({
                  ...badgeForm,
                  points_required: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Criteria Type</InputLabel>
                <Select
                  value={badgeForm.criteria_type}
                  onChange={(e) => setBadgeForm({ ...badgeForm, criteria_type: e.target.value })}
                  label="Criteria Type"
                >
                  <MenuItem value="points">Total Points</MenuItem>
                  <MenuItem value="courses_completed">Courses Completed</MenuItem>
                  <MenuItem value="perfect_scores">Perfect Test Scores</MenuItem>
                  <MenuItem value="streak_days">Login Streak (Days)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Criteria Value"
                value={badgeForm.criteria_value}
                onChange={(e) => setBadgeForm({
                  ...badgeForm,
                  criteria_value: parseInt(e.target.value) || 0
                })}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, badge: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateBadge}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, badge: null })}
      >
        <DialogTitle>Delete Badge</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the badge "{deleteDialog.badge?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. Students who have earned this badge will keep it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, badge: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteBadge}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BadgeManagement;